import type { TourStepPlacement } from "../tour.types";

export interface RectLike {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface SizeLike {
  width: number;
  height: number;
}

export type ResolvedPlacement = Exclude<TourStepPlacement, "auto">;

export interface StepCardPosition {
  top: number;
  left: number;
  placement: ResolvedPlacement;
}

export interface PositionStepCardOptions {
  targetRect: RectLike;
  cardSize: SizeLike;
  viewport: SizeLike;
  preferred?: TourStepPlacement;
  /** Gap in px between the target and the card */
  offset?: number;
}

/** Minimum distance in px kept between the card and the viewport edges */
export const VIEWPORT_MARGIN = 8;

const DEFAULT_PLACEMENT_ORDER: ResolvedPlacement[] = ["bottom", "top", "right", "left"];

const placementOrder = (preferred?: TourStepPlacement): ResolvedPlacement[] => {
  if (!preferred || preferred === "auto") return DEFAULT_PLACEMENT_ORDER;
  return [preferred, ...DEFAULT_PLACEMENT_ORDER.filter((placement) => placement !== preferred)];
};

const fits = (placement: ResolvedPlacement, target: RectLike, card: SizeLike, viewport: SizeLike, offset: number): boolean => {
  switch (placement) {
    case "bottom":
      return target.top + target.height + offset + card.height <= viewport.height - VIEWPORT_MARGIN;
    case "top":
      return target.top - offset - card.height >= VIEWPORT_MARGIN;
    case "right":
      return target.left + target.width + offset + card.width <= viewport.width - VIEWPORT_MARGIN;
    case "left":
      return target.left - offset - card.width >= VIEWPORT_MARGIN;
  }
};

const coordinatesFor = (placement: ResolvedPlacement, target: RectLike, card: SizeLike, offset: number): { top: number; left: number } => {
  const centeredLeft = target.left + target.width / 2 - card.width / 2;
  const centeredTop = target.top + target.height / 2 - card.height / 2;
  switch (placement) {
    case "bottom":
      return { top: target.top + target.height + offset, left: centeredLeft };
    case "top":
      return { top: target.top - offset - card.height, left: centeredLeft };
    case "right":
      return { top: centeredTop, left: target.left + target.width + offset };
    case "left":
      return { top: centeredTop, left: target.left - offset - card.width };
  }
};

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Places the step card next to the spotlighted target: tries the preferred
 * placement first, flips through the remaining sides on viewport overflow,
 * then clamps the final coordinates inside the viewport.
 */
export function positionStepCard({ targetRect, cardSize, viewport, preferred, offset = 12 }: PositionStepCardOptions): StepCardPosition {
  const order = placementOrder(preferred);
  const placement = order.find((candidate) => fits(candidate, targetRect, cardSize, viewport, offset)) ?? order[0] ?? "bottom";
  const { top, left } = coordinatesFor(placement, targetRect, cardSize, offset);
  return {
    placement,
    top: clamp(top, VIEWPORT_MARGIN, viewport.height - cardSize.height - VIEWPORT_MARGIN),
    left: clamp(left, VIEWPORT_MARGIN, viewport.width - cardSize.width - VIEWPORT_MARGIN),
  };
}
